import { useEffect, useState } from "react";
import { Loader2, RefreshCw, WifiOff } from "lucide-react";

/**
 * Shown when the app could not boot because the very first request never reached the server.
 *
 * Strictly a BOOT screen. Until now an offline cold start fell through `TenantProvider`'s catch with no error
 * state at all, so the app rendered unbranded (the theme is applied from the tenant doc, which never arrived)
 * with empty sections, and every guarded route bounced to /login — which reads as "the app is broken, and it
 * logged me out". Nothing here touches authentication: the session is left exactly as it was, so reconnecting
 * restores it without an OTP.
 *
 * It never replaces a running app. Once the boot has succeeded the provider stops rendering this, so losing
 * connectivity mid-session leaves the current screen, the cart, the order flow and the alert polling alone.
 */
export default function OfflineGate({ onRetry, brand = null }) {
    const [busy, setBusy] = useState(false);
    // ``navigator.onLine`` is only trustworthy in the negative: false genuinely means the device has no
    // connection, while true merely means it has *a* network — which may be a Wi-Fi with no route out, or a
    // backend that is down. Word each case for what we actually know rather than blaming the user's phone for
    // an outage on our side.
    const [deviceOffline, setDeviceOffline] = useState(() => {
        try { return navigator.onLine === false; } catch { return false; }
    });

    useEffect(() => {
        const sync = () => {
            try { setDeviceOffline(navigator.onLine === false); } catch { /* ignore */ }
        };
        window.addEventListener("online", sync);
        window.addEventListener("offline", sync);
        return () => {
            window.removeEventListener("online", sync);
            window.removeEventListener("offline", sync);
        };
    }, []);

    const retry = async () => {
        if (busy) return;
        setBusy(true);
        try {
            await onRetry?.();
        } finally {
            // If the retry worked the provider unmounts this component; the guard is for the failing case.
            setBusy(false);
        }
    };

    const title = deviceOffline ? "No internet connection" : "Can't reach the server right now";
    const help = deviceOffline
        ? "Check your mobile data or Wi-Fi and try again. You're still signed in — nothing has been lost."
        : "Your device is connected, but we couldn't reach us. This is usually temporary. You're still signed in.";

    return (
        <div
            className="min-h-screen flex flex-col items-center justify-center p-6 text-center bg-background safe-pl safe-pr"
            data-testid="offline-gate"
            role="alert"
        >
            <div className="w-20 h-20 rounded-2xl bg-muted inline-flex items-center justify-center mb-5">
                <WifiOff size={38} className="text-muted-foreground" aria-hidden="true" />
            </div>
            <h1 className="text-2xl font-bold mb-2" data-testid="offline-gate-title">{title}</h1>
            <p className="text-sm text-muted-foreground max-w-sm mb-6 leading-relaxed" data-testid="offline-gate-message">
                {help}
            </p>
            <button
                type="button"
                onClick={retry}
                disabled={busy}
                data-testid="offline-gate-retry"
                className="touch-target bg-primary text-primary-foreground rounded-full px-7 py-3 font-bold text-sm inline-flex items-center gap-2 disabled:opacity-60 active:scale-[0.98] transition"
            >
                {busy ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
                {busy ? "Checking…" : "Retry"}
            </button>
            {brand ? <p className="text-xs text-muted-foreground/70 mt-8">{brand}</p> : null}
        </div>
    );
}
